import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { API } from "./cache";
import { downloadAttachment } from "./shared/utils";
import "./AttachmentsModal.css";

// ── Constants (must match app/schemas/attachment.py) ──────────────────────────
const ALLOWED_CATEGORIES = ["Tasks", "Defects", "Meetings", "Releases", "Dependencies", "Others"];
const DEFAULT_MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const ACCEPT  = ".pdf,.xls,.xlsx,.csv,.doc,.docx,.txt,.jpg,.jpeg,.png,.mp4,.mov,.avi,.zip,.rar";

// ── Helpers ───────────────────────────────────────────────────────────────────

async function apiFetch(url, opts = {}) {
  const res = await fetch(url, { credentials: "include", ...opts });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Error ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

function fileIcon(fileType) {
  if (!fileType) return "📎";
  if (fileType.startsWith("image"))                                   return "🖼";
  if (fileType.includes("pdf"))                                       return "📄";
  if (fileType.includes("sheet") || fileType.includes("excel") ||
      fileType.includes("csv"))                                       return "📊";
  if (fileType.includes("word") || fileType.includes("doc"))         return "📝";
  return "📎";
}

function fmtSize(kb) {
  if (!kb && kb !== 0) return "";
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtMaxFileSize(bytes) {
  if (!bytes || bytes <= 0) return "2 MB";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
  return `${Math.ceil(bytes / 1024)} KB`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AttachmentsModal({
  refType,
  refId,
  title,
  onClose,
  autoOpenForm = false,
  defaultCaption = "",
  lockCaption = false,
  maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE_BYTES,
}) {
  const [attachments, setAttachments] = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");

  // Upload form state
  const [showForm,    setShowForm]    = useState(autoOpenForm);
  const [file,        setFile]        = useState(null);   // File object
  const [caption,     setCaption]     = useState(defaultCaption);
  const [fileError,   setFileError]   = useState("");     // client-side validation
  const [uploading,   setUploading]   = useState(false);

  const [deleting,    setDeleting]    = useState(null);   // id being deleted

  const fileInputRef = useRef(null);

  // ── Load list ───────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch(`${API}/attachments/${refType}/${refId}`);
      setAttachments(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [refType, refId]);

  useEffect(() => {
    load();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [load, onClose]);

  // ── File picker ─────────────────────────────────────────────────────────────

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    setFileError("");
    if (!f) { setFile(null); return; }
    if (f.size > maxFileSizeBytes) {
      setFileError(`File exceeds the ${fmtMaxFileSize(maxFileSizeBytes)} limit. Please choose a smaller file.`);
      setFile(null);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const resetForm = () => {
    setFile(null);
    setCaption(defaultCaption);
    setFileError("");
    setError("");
    setShowForm(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (showForm && defaultCaption) setCaption(defaultCaption);
  }, [showForm, defaultCaption]);

  // ── Upload ──────────────────────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!file || !caption) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file",     file);
      fd.append("ref_type", refType);
      fd.append("ref_id",   refId);
      fd.append("caption",  caption);
      // Do NOT set Content-Type — browser adds the multipart boundary automatically
      await apiFetch(`${API}/attachments/upload`, { method: "POST", body: fd });
      resetForm();
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // ── Download ────────────────────────────────────────────────────────────────

  const handleDownload = async (id) => {
    setError("");
    try {
      await downloadAttachment(id);
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    setDeleting(id);
    setError("");
    try {
      await apiFetch(`${API}/attachments/${id}`, { method: "DELETE" });
      setAttachments(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return createPortal(
    <div className="att-overlay" onClick={onClose}>
      <div className="att-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="att-header">
          <span className="att-header__title">📎 {title || "Attachments"}</span>
          <button className="att-header__close" onClick={onClose} type="button">✕</button>
        </div>

        {/* Toolbar / upload form */}
        <div className="att-toolbar">
          {!showForm ? (
            <button
              className="att-btn att-btn--primary"
              onClick={() => setShowForm(true)}
              type="button"
            >
              + Attach File
            </button>
          ) : (
            <div className="att-form">

              {/* File picker row */}
              <div className="att-upload-zone">
                <input
                  ref={fileInputRef}
                  id="att-file-input"
                  type="file"
                  accept={ACCEPT}
                  className="att-file-input"
                  onChange={handleFileChange}
                />
                <label htmlFor="att-file-input" className="att-file-label">
                  {file ? (
                    <span className="att-file-chosen">
                      <span className="att-file-chosen__icon">{fileIcon(file.type)}</span>
                      <span className="att-file-chosen__name">{file.name}</span>
                      <span className="att-file-chosen__size">{fmtSize(Math.ceil(file.size / 1024))}</span>
                    </span>
                  ) : (
                    <span className="att-file-placeholder">
                      Choose a file&nbsp;
                      <span className="att-file-hint">PDF, Excel, CSV, Word, TXT, image, video, ZIP/RAR · max {fmtMaxFileSize(maxFileSizeBytes)}</span>
                    </span>
                  )}
                </label>
              </div>

              {fileError && <div className="att-file-error">{fileError}</div>}

              {/* Category row */}
              <div className="att-form-row">
                <select
                  className="att-form-select"
                  value={caption}
                  onChange={e => setCaption(e.target.value)}
                  disabled={lockCaption}
                >
                  {lockCaption ? (
                    <option value={defaultCaption}>{defaultCaption}</option>
                  ) : (
                    <>
                      <option value="">Select category *</option>
                      {ALLOWED_CATEGORIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              {/* Actions */}
              <div className="att-form-actions">
                <button
                  className="att-btn att-btn--primary"
                  onClick={handleUpload}
                  disabled={uploading || !file || !caption || !!fileError}
                  type="button"
                >
                  {uploading ? (
                    <><span className="att-spinner" />Uploading…</>
                  ) : "Upload"}
                </button>
                <button
                  className="att-btn att-btn--ghost"
                  onClick={resetForm}
                  disabled={uploading}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error banner */}
        {error && <div className="att-error">{error}</div>}

        {/* Attachment list */}
        <div className="att-list">
          {loading ? (
            <div className="att-empty">Loading…</div>
          ) : !attachments || attachments.length === 0 ? (
            <div className="att-empty">No attachments yet.</div>
          ) : (
            attachments.map(a => (
              <div key={a.id} className="att-card">
                <span className="att-card__icon">{fileIcon(a.file_type)}</span>
                <div className="att-card__info">
                  <button
                    type="button"
                    onClick={() => handleDownload(a.id)}
                    className="att-card__name"
                    title={a.file_name}
                    style={{ background: "none", border: "none", padding: 0, font: "inherit", textAlign: "left", cursor: "pointer" }}
                  >
                    {a.file_name}
                  </button>
                  <div className="att-card__meta">
                    {a.file_type && (
                      <span className="att-card__tag">{a.file_type.split("/").pop()}</span>
                    )}
                    {a.file_size_kb > 0 && (
                      <span className="att-card__tag att-card__tag--size">
                        {fmtSize(a.file_size_kb)}
                      </span>
                    )}
                    {a.caption && (
                      <span className="att-card__tag att-card__tag--cat">{a.caption}</span>
                    )}
                    {a.uploader_name && (
                      <span className="att-card__by">by {a.uploader_name}</span>
                    )}
                    {a.created_at && (
                      <span className="att-card__date">{fmtDate(a.created_at)}</span>
                    )}
                  </div>
                </div>
                <button
                  className="att-card__del"
                  onClick={() => handleDelete(a.id)}
                  disabled={deleting === a.id}
                  title="Delete attachment"
                  type="button"
                >
                  {deleting === a.id ? "…" : "✕"}
                </button>
              </div>
            ))
          )}
        </div>

      </div>
    </div>,
    document.body
  );
}
